import React, { useState, useEffect } from 'react';
import { Modal, Box, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import IDCardViewer from './IDCardViewer';
import SelfieCardViewer from './SelfieCardViewer';
import RejectReasonsModal from './RejectReasonsModal';
import { verifyUser } from '../../api/user';
import { useNotification } from '../../hooks/useNotification.js';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 622,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '20px',
  maxHeight: '90vh',
  overflow: 'auto',
  p: '0px 30px 0 30px',
};

export const reasons = [
  {
    id: 1,
    title: 'Incomplete Documents',
    description:
      'The uploaded document is missing required pages or sections. Example: Only one side of an ID card is uploaded, or critical information like name or date of birth is not visible.',
  },
  {
    id: 2,
    title: 'Blurry or Unreadable Images',
    description:
      'The document or photo is not clear due to poor quality, low resolution, or bad lighting. Example: Text on the document/fields of the photo is too dark to verify.',
  },
  {
    id: 3,
    title: 'Mismatched Information',
    description:
      "Details on the document do not match the user's profile information. Example: The name or birthdate on the document does not align with the user's registered details.",
  },
  {
    id: 4,
    title: 'Expired Documents',
    description:
      "The provided document is no longer valid or has passed its expiration date. Example: A driver's license that expired six months ago.",
  },
  {
    id: 5,
    title: 'Fake or Forged Documents',
    description:
      'The document appears to be counterfeit, altered, or otherwise fraudulent. Example: Discrepancies in document design, altered dates, or mismatched holograms.',
  },
  {
    id: 6,
    title: 'Unacceptable Document Type',
    description:
      'The submitted document is not on the list of accepted types for verification. Example: A library card is submitted instead of a government-issued ID.',
  },
  {
    id: 7,
    title: 'Obstructed or Cropped Images',
    description:
      'Parts of the document are cut off or hidden. Example: A passport photo where the MRZ (machine-readable zone) is not visible.',
  },
  {
    id: 8,
    title: 'Duplicate Submissions',
    description:
      'The same document has already been submitted and rejected. Example: A user resubmits the same invalid document without making corrections.',
  },
  {
    id: 9,
    title: 'Unauthorized Submission',
    description:
      'The document belongs to someone else, not the account holder. Example: Submitting a friend’s or family member’s ID instead of their own.',
  },
  {
    id: 10,
    title: 'Language Barrier',
    description:
      'The document is in a language that cannot be verified, and no translation was provided. Example: A non-English document submitted without certified translation.',
  },
];

function VerificationModal({
  open,
  handleCloseModal,
  selectedVerificationType,
  handleRequestReUpload,
  handleReject,
  user,
  fetchData
}) {
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const [step, setStep] = useState(1);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [customReason, setCustomReason] = useState('');

  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedReasons([]);
      setCustomReason('');
    }
  }, [open]);

  const handleBack = () => {
    if (step > 1) setStep((prevStep) => prevStep - 1); // Go back one step
    else handleCloseModal(); // Close modal if already at the first step
  };

  const handleNextToRejectReasons = () => {
    setStep(2); // Go to the RejectReasonsModal
  };

  const handleSubmit = async (value, verificationType) => {
    if (value === 'reject' || value === 'reupload') {
      if (selectedReasons?.length === 0 && customReason === '') {
        return showErrorNotification('Please select a valid reason');
      }
    }

    let rejectReason =
      selectedReasons.length > 0
        ? `${reasons[selectedReasons[0] - 1].title}: ${reasons[selectedReasons[0] - 1].description
        }`
        : customReason;

    try {
      const res = await verifyUser(
        user._id,
        value,
        verificationType,
        rejectReason
      );
      if (res.success) {
        if (value === 'approve') {
          showSuccessNotification(`User verification successfully approved`);
        } else if (value === 'reject') {
          showSuccessNotification(`User verification rejected`);
        } else {
          showSuccessNotification(`User re-upload required`);
        }
        fetchData()
      } else {
        showErrorNotification(
          res.message || 'Something went wrong, please try again.'
        );
      }
    } catch (error) {
      showErrorNotification(
        'An error occurred while updating the document. Please try again.'
      );
    }

    handleCloseModal();
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          className="d-flex align-items-center justify-content-between gap-2 mb-24"
          style={{
            position: 'sticky',
            top: '-1px',
            background: '#fff',
            padding: '20px 0',
          }}
        >
          <Box className="d-flex align-items-center gap-2">
            {/* <Button onClick={handleBack}>
              <KeyboardBackspaceIcon />
            </Button> */}
            <h4 className="text-black mb-0">
              {selectedVerificationType === 'ID Verification'
                ? 'ID Verification'
                : selectedVerificationType === 'Selfie Verification'
                  ? 'Selfie Verification'
                  : 'Verification'}
            </h4>
          </Box>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Conditional Content */}
        {step === 1 && selectedVerificationType === 'ID Verification' && (
          <IDCardViewer
            frontImage={user?.documents?.front}
            backImage={user?.documents?.back}
            documentNo={user?.documents?.number}
            documentType={user?.documents?.type}
          />
        )}
        {step === 1 && selectedVerificationType === 'Selfie Verification' && (
          <SelfieCardViewer image={user.realTimePicture} />
        )}
        {step === 2 && (
          <RejectReasonsModal
            selectedReasons={selectedReasons}
            setSelectedReasons={setSelectedReasons}
            customReason={customReason}
            setCustomReason={setCustomReason}
          />
        )}

        {/* Footer Actions */}
        <div
          className="d-flex justify-content-between align-items-center gap-2"
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            padding: '20px 0',
          }}
        >
          {step === 1 && selectedVerificationType === 'ID Verification' && (
            <>
              {/* <button
                className="badge text-sm fw-medium border border bg-transparent px-20 py-12 radius-6 text-secondary-light w-100"
                onClick={handleNextToRejectReasons}
              >
                Request Re-Upload
              </button> */}
              <button
                className="badge text-sm fw-medium border border bg-transparent px-20 py-12 radius-6 text-secondary-light w-100"
                onClick={handleNextToRejectReasons}
              >
                Reject
              </button>
              <button
                className="badge text-sm fw-medium bg-warning-900 px-20 py-12 radius-6 text-white w-100"
                onClick={() =>
                  handleSubmit('approve', selectedVerificationType)
                }
              >
                Approved
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button
                className="badge text-sm fw-medium border border bg-transparent px-20 py-12 radius-6 text-secondary-light w-100"
                onClick={handleBack}
              >
                Back
              </button>
              {/* <button
                className="badge text-sm fw-medium border border bg-transparent px-20 py-12 radius-6 text-secondary-light w-100"
                // onClick={handleRequestReUpload}
                onClick={() =>
                  handleSubmit('reupload', selectedVerificationType)
                }
              >
                Request Re-Upload
              </button> */}
              <button
                className="badge text-sm fw-medium border border bg-transparent px-20 py-12 radius-6 text-secondary-light w-100"
                // onClick={handleReject}
                onClick={() => handleSubmit('reject', selectedVerificationType)}
              >
                Reject
              </button>
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default VerificationModal;
